// Study Hall client state (DESIGN.md 11 and 14): the `ready` snapshot, one message buffer per room
// merged by id, the `useq` resume counter, typing, blocks, reactions and the send outbox. Reducers
// only, no DOM, so test/chat.client.test.mjs drives it with protocol frames directly.
//
// Buffers hold a contiguous window of a room: the latest rows, plus older pages loaded with `more`.
// A window capped at `cap` rows drops its oldest rows as new ones arrive (`more` = older exist), or,
// when older pages pushed it over the cap, its newest rows (`detached` = the latest aren't held; a
// detached room ignores live rows and resumes with a reset). `since` is only offered for a room
// whose window is in memory and attached, since a delta applied to nothing would show only the rows
// that changed.

export const CAP = 200;
export const CAP_LITE = 120;
export const GROUP_MS = 5 * 60 * 1000;
export const TYPING_MS = 6000;
export const MAX_EDITS = 5;
export const OUTBOX_MAX = 5;
export const REACTIONS = ["\u{1F44D}", "\u{2764}\u{FE0F}", "\u{1F602}", "\u{1F389}", "\u{1F914}", "\u{2705}"];

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const str = (v) => (typeof v === "string" ? v : "");

function normX(x) {
	const out = [0, 0, 0, 0, 0, 0];
	if (Array.isArray(x)) for (let i = 0; i < 6; i++) out[i] = Math.max(0, num(x[i]));
	return out;
}

// A poll's options, closing time and tallies (counts only, never who voted).
function normPoll(p) {
	if (!p || typeof p !== "object" || !Array.isArray(p.o)) return null;
	const o = p.o.slice(0, 4).map(str);
	return { o, until: num(p.until), pv: o.map((_, i) => Math.max(0, num(Array.isArray(p.pv) ? p.pv[i] : 0))), few: !!p.few };
}
// A room timer: { ph: idle | focus | break, at, end, cyc, n, plan }.
export function normTmr(t) {
	if (!t || typeof t !== "object") return null;
	const ph = t.ph === "focus" || t.ph === "break" ? t.ph : "idle";
	return { ph, at: num(t.at), end: num(t.end), cyc: num(t.cyc), n: Math.max(0, num(t.n)), plan: str(t.plan) };
}
// "18:42" from milliseconds left.
export function clock(ms) {
	const s = Math.max(0, Math.ceil(ms / 1000));
	const m = Math.floor(s / 60), r = s % 60;
	return m + ":" + String(r).padStart(2, "0");
}

// The wire shape M (11.4), copied into plain fields so nothing unexpected rides along.
export function normM(m) {
	if (!m || typeof m !== "object" || !Number.isFinite(m.id)) return null;
	const a = m.a && typeof m.a === "object" ? m.a : {};
	const rp = m.rp && typeof m.rp === "object" && Number.isFinite(m.rp.id) ? { id: m.rp.id, h: str(m.rp.h), ex: str(m.rp.ex) } : null;
	return {
		id: m.id, room: str(m.room), useq: num(m.useq),
		a: { h: str(a.h), u: str(a.u), s: num(a.s), f: str(a.f) },
		b: str(m.b), rp, mn: Array.isArray(m.mn) ? m.mn.filter((u) => typeof u === "string") : [],
		at: num(m.at), ed: num(m.ed), revs: num(m.revs), st: num(m.st), fr: num(m.fr), x: normX(m.x),
		k: m.k === 1 ? 1 : 0, poll: m.k === 1 ? normPoll(m.poll) : null,
	};
}

// Pinned resources: a Re:Vault tool by id, or a staff link (https only; the server checked it).
const TOOL_RE = /^[a-z]{2,20}$/;
export function normPins(list) {
	if (!Array.isArray(list)) return [];
	const out = [];
	for (const p of list.slice(0, 5)) {
		if (!p || typeof p !== "object" || typeof p.label !== "string" || !p.label) continue;
		if (p.k === "tool" && typeof p.id === "string" && TOOL_RE.test(p.id)) out.push({ k: "tool", id: p.id, label: p.label });
		else if (p.k === "link" && typeof p.url === "string" && /^https:\/\//i.test(p.url)) out.push({ k: "link", label: p.label, url: p.url });
	}
	return out;
}

function normLockdown(l) { return { level: Math.max(0, Math.min(3, num(l && l.level))), until: num(l && l.until), quiet: !!(l && l.quiet) }; }

// Local calendar day of a timestamp, for date separators and grouping.
export function dayKey(at) { const d = new Date(at); return d.getFullYear() + "-" + d.getMonth() + "-" + d.getDate(); }

// Consecutive rows from the same author within 5 minutes on the same day are grouped (a reply
// always starts a new group, so its preview line has a header to sit under).
export function groupable(prev, m) {
	return !!prev && !m.rp && prev.a.u === m.a.u && prev.a.s === m.a.s && m.at - prev.at >= 0 && m.at - prev.at < GROUP_MS && dayKey(prev.at) === dayKey(m.at);
}

// A stable hue per public user key, for the monogram circle.
export function hue(u) {
	let h = 2166136261;
	for (let i = 0; i < u.length; i++) { h ^= u.charCodeAt(i); h = Math.imul(h, 16777619); }
	return (h >>> 0) % 360;
}

// A client idempotency key: 16 characters of [A-Za-z0-9_-] (the server wants 8 to 24).
export function newCid(rand = (n) => crypto.getRandomValues(new Uint8Array(n))) {
	const A = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
	let s = "";
	for (const b of rand(16)) s += A[b & 63];
	return s;
}

export function createStore({ cap = CAP, now = Date.now } = {}) {
	let clockOffset = 0;
	const serverNow = () => now() + clockOffset;
	function syncClock(d) { if (Number.isFinite(d.now)) clockOffset = d.now - now(); }
	const S = {
		me: null, gate: null, mode: "open", lockdown: { level: 0, until: 0 }, lockdownHit: false,
		rooms: [], focus: "",
		limits: { maxLen: 1000, maxLines: 20, editWindowS: 900, maxLinks: 3, maxMentions: 5 },
		reactions: REACTIONS.slice(), blocks: new Set(),
		bufs: new Map(), mine: new Map(), typing: new Map(), pending: [], modq: { open: 0, urgent: 0 },
		// The focus lease this account holds ({ room, cyc } or null) and your vote per poll id.
		lease: null, votes: new Map(),
	};

	const room = (id) => S.rooms.find((r) => r.id === id) || null;
	function buf(id) {
		let b = S.bufs.get(id);
		if (!b) { b = { ids: [], by: new Map(), more: false, detached: false, loaded: false, useq: 0 }; S.bufs.set(id, b); }
		return b;
	}
	const list = (id) => { const b = S.bufs.get(id); return b ? b.ids.map((i) => b.by.get(i)) : []; };

	// Binary-search insert by id; replaces a row already held (an edit's ack keeps its replies' previews current).
	function put(b, m) {
		if (b.by.has(m.id)) { b.by.set(m.id, m); syncReplies(b, m); return; }
		let lo = 0, hi = b.ids.length;
		while (lo < hi) { const mid = (lo + hi) >> 1; if (b.ids[mid] < m.id) lo = mid + 1; else hi = mid; }
		b.ids.splice(lo, 0, m.id);
		b.by.set(m.id, m);
	}
	function drop(b, id) { b.by.delete(id); S.mine.delete(id); }
	// The server renders a reply preview from the parent's current text (the first 80 characters,
	// blank once it is hidden), so an edit or a removal changes the preview under every reply.
	function syncReplies(b, parent) {
		const ex = parent.st === 0 ? Array.from(parent.b).slice(0, 80).join("") : "";
		for (const m of b.by.values()) if (m.rp && m.rp.id === parent.id) m.rp.ex = ex;
	}
	function trim(b, keepOld) {
		while (b.ids.length > cap) {
			if (keepOld) { drop(b, b.ids.pop()); b.detached = true; b.useq = 0; }
			else { drop(b, b.ids.shift()); b.more = true; }
		}
	}
	function seen(b, useq) { if (!b.detached && useq > b.useq) b.useq = useq; }
	function setMine(obj) {
		if (!obj || typeof obj !== "object") return;
		for (const [id, es] of Object.entries(obj)) if (Array.isArray(es)) S.mine.set(Number(id), new Set(es.filter((e) => e >= 0 && e <= 5)));
	}

	function ready(d) {
		syncClock(d);
		S.me = d.me && typeof d.me === "object" ? { ...d.me } : null;
		S.gate = d.gate || null;
		S.mode = str(d.mode) || "open";
		S.lockdown = normLockdown(d.lockdown);
		S.rooms = (Array.isArray(d.rooms) ? d.rooms : []).filter((r) => r && typeof r.id === "string").map((r) => ({
			id: r.id, name: str(r.name) || r.id, topic: str(r.topic), kind: str(r.kind) || "room", slow: num(r.slow), locked: !!r.locked,
			canPost: r.canPost === undefined ? undefined : !!r.canPost, head: num(r.head), read: num(r.read), ment: num(r.ment), here: num(r.here),
			rule: str(r.rule), polls: !!r.polls, inv: !!r.inv, retired: !!r.retired, tmr: normTmr(r.tmr),
			subject: !!r.subject, pins: normPins(r.pins), qotd: r.qotd ? normM(r.qotd) : null,
		}));
		S.lease = normLease(d.lease);
		S.blocks = new Set(Array.isArray(d.blocks) ? d.blocks.filter((u) => typeof u === "string") : []);
		if (d.limits && typeof d.limits === "object") for (const k of Object.keys(S.limits)) if (Number.isFinite(d.limits[k])) S.limits[k] = d.limits[k];
		if (Array.isArray(d.reactions) && d.reactions.length === 6) S.reactions = d.reactions.map(String);
		const ids = new Set(S.rooms.map((r) => r.id));
		for (const k of [...S.bufs.keys()]) if (!ids.has(k)) S.bufs.delete(k);
		if (ids.has(d.focus)) S.focus = d.focus;
		else if (!ids.has(S.focus)) S.focus = ids.has("quiet") ? "quiet" : S.rooms[0] ? S.rooms[0].id : "";
		// Lockdown 2 turns on by account age, which the client can't see; the server's canPost says.
		S.lockdownHit = S.rooms.some((r) => r.kind === "room" && !r.locked && r.canPost === false);
	}

	function hist(d) {
		const rows = (Array.isArray(d.m) ? d.m : []).map(normM).filter((m) => m && (!m.room || m.room === d.room));
		for (const m of rows) m.room = d.room;
		const b = buf(d.room);
		if (d.mode === "older") {
			const oldest = b.ids.length ? b.ids[0] : Infinity;
			for (const m of rows) if (m.id < oldest) put(b, m);
			b.more = !!d.more;
			trim(b, true);
		} else if (d.mode === "delta" && b.loaded && !b.detached) {
			const oldest = b.ids.length ? b.ids[0] : -Infinity;
			for (const m of rows) if (m.id >= oldest) { put(b, m); seen(b, m.useq); }
			trim(b, false);
		} else {
			for (const id of b.ids) S.mine.delete(id);
			b.ids = []; b.by.clear(); b.detached = false; b.useq = 0;
			for (const m of rows) { put(b, m); seen(b, m.useq); }
			b.more = !!d.more;
			trim(b, false);
		}
		b.loaded = true;
		setMine(d.mine);
		setVotes(d.mv);
		return b;
	}
	function setVotes(obj) {
		if (!obj || typeof obj !== "object") return;
		for (const [id, opt] of Object.entries(obj)) if (Number.isInteger(opt) && opt >= 0 && opt <= 3) S.votes.set(Number(id), opt);
	}
	function normLease(l) { return l && typeof l === "object" && typeof l.room === "string" && room(l.room) ? { room: l.room, cyc: num(l.cyc) } : null; }

	// A room timer frame (a phase change, or a new focus count). Returns the room, or null.
	function tmr(d) {
		const r = room(d.room);
		const t = normTmr(d);
		if (!r || !t) return null;
		const prev = r.tmr;
		t.plan = prev ? prev.plan : "";
		r.tmr = t;
		// Our lease ends with the room going idle, and follows the room into each new cycle while we
		// stay connected (the server rolls it; the client only mirrors that).
		if (S.lease && S.lease.room === r.id) {
			if (t.ph === "idle") S.lease = null;
			else if (t.cyc > S.lease.cyc) S.lease = { room: r.id, cyc: t.cyc };
		}
		return { room: r, changed: !prev || prev.ph !== t.ph || prev.cyc !== t.cyc };
	}
	function setLease(l) { S.lease = normLease(l); }
	// The timed room most people are focusing in, for Hangout's "next break" line.
	function busiest() {
		let best = null;
		for (const r of S.rooms) if (r.tmr && r.tmr.ph !== "idle" && (!best || r.tmr.n > best.tmr.n)) best = r;
		return best;
	}
	function myVote(id) { return S.votes.has(id) ? S.votes.get(id) : -1; }
	function setTally(poll, d) {
		poll.pv = poll.o.map((_, i) => Math.max(0, num(d.pv[i])));
		poll.few = !!d.few;
	}
	function setVote(id, opt, d) {
		if (opt >= 0) S.votes.set(id, opt); else S.votes.delete(id);
		const row = findRow(id);
		if (row && row.poll && d && Array.isArray(d.pv)) setTally(row.poll, d);
		return row;
	}

	// A live row (`msg`, or the `ack {m}` of our own send). Returns true when it was added.
	function msg(raw) {
		const m = normM(raw);
		if (!m) return false;
		const b = buf(m.room);
		if (!b.loaded || b.detached) return false;
		put(b, m);
		seen(b, m.useq);
		trim(b, false);
		const r = room(m.room);
		if (r && m.id > r.head) r.head = m.id;
		return true;
	}

	// Returns {row, needBody}: needBody when a restored message's text wasn't included.
	function upd(d) {
		const qr = room(d.room);
		if (qr && qr.qotd && qr.qotd.id === d.id && "st" in d && num(d.st) !== 0) qr.qotd = null;
		const b = S.bufs.get(d.room);

		if (!b || !b.loaded) return { row: null, needBody: false };
		seen(b, num(d.useq));
		const row = b.by.get(d.id);
		if (!row) return { row: null, needBody: false };
		const wasHidden = row.st !== 0;
		if ("st" in d) row.st = num(d.st);
		if (typeof d.b === "string") row.b = d.b;
		else if ("st" in d && row.st !== 0) row.b = "";
		for (const k of ["ed", "revs", "fr"]) if (k in d) row[k] = num(d[k]);
		if ("x" in d) row.x = normX(d.x);
		if (Array.isArray(d.pv) && row.poll) setTally(row.poll, d);
		if ("st" in d && row.st !== 0 && !staff()) row.poll = null;
		if (num(d.useq) > row.useq) row.useq = num(d.useq);
		if (row.st !== 0 || typeof d.b === "string") syncReplies(b, row);
		// A question of the day that was removed or edited follows its message.
		const r = room(d.room);
		if (r && r.qotd && r.qotd.id === row.id) r.qotd = row.st === 0 ? { ...row } : null;

		return { row, needBody: wasHidden && row.st === 0 && typeof d.b !== "string" };
	}

	function roomEv(d) {
		const r = room(d.id);
		if (!r) return;
		if ("slow" in d) r.slow = num(d.slow);
		if ("locked" in d) r.locked = !!d.locked;
		if ("inv" in d) r.inv = !!d.inv;
		if (typeof d.topic === "string") r.topic = d.topic;
		if ("qotd" in d) r.qotd = d.qotd ? normM(d.qotd) : null;
		r.canPost = undefined; // the server's earlier answer no longer holds
	}
	function heads(d) {
		const rs = d && d.rooms && typeof d.rooms === "object" ? d.rooms : {};
		for (const [id, v] of Object.entries(rs)) {
			const r = room(id);
			if (!r || !Array.isArray(v)) continue;
			if (num(v[0]) > r.head) r.head = num(v[0]);
			r.ment += Math.max(0, num(v[1]));
		}
	}
	function here(d) { const r = room(d.room); if (r) r.here = Math.max(0, num(d.n)); }
	function read(d) {
		const r = room(d.room);
		if (!r) return;
		if (num(d.id) > r.read) r.read = num(d.id);
		if (r.read >= r.head) r.ment = 0;
	}
	function you(d) {
		syncClock(d);
		if (!S.me) S.me = {};
		for (const k of ["staff", "probation", "probationUntil", "trusted", "sanction", "pres"]) if (k in d) S.me[k] = d[k];
	}
	function lockdown(d) {
		S.lockdown = normLockdown(d);
		S.lockdownHit = false;
		for (const r of S.rooms) r.canPost = undefined;
	}
	// `us` is the public key of each name in `who` (same order), so a blocked person stays hidden
	// even with no rows of theirs loaded to match the handle against.
	function typing(d) {
		const w = Array.isArray(d.who) ? d.who.slice(0, 3) : [], u = Array.isArray(d.us) ? d.us.slice(0, 3) : [];
		const who = [], us = [];
		w.forEach((h, i) => { if (typeof h === "string") { who.push(h); us.push(str(u[i])); } });
		S.typing.set(d.room, { who, us, more: Math.max(0, num(d.more)), at: now() });
	}

	// "sam is typing" and friends, minus yourself and people you blocked.
	function typingLine(id, fmt) {
		const t = S.typing.get(id);
		if (!t || now() - t.at > TYPING_MS) return "";
		const hide = blockedHandles(id);
		if (S.me && S.me.h) hide.add(S.me.h);
		const me = S.me && S.me.u;
		const who = t.who.filter((h, i) => !hide.has(h) && !(t.us[i] && (S.blocks.has(t.us[i]) || t.us[i] === me)));
		if (!who.length) return "";
		if (!t.more) return fmt(who, 0);
		const shown = who.slice(0, 2);
		return fmt(shown, who.length + t.more - shown.length);
	}
	function blockedHandles(id) {
		const out = new Set();
		if (!S.blocks.size) return out;
		for (const m of list(id)) if (S.blocks.has(m.a.u)) out.add(m.a.h);
		return out;
	}

	// Handles seen in a room, most recent speaker first: the only mention "directory" there is.
	function authors(id) {
		const seenU = new Set(), out = [];
		const rows = list(id);
		for (let i = rows.length - 1; i >= 0; i--) {
			const a = rows[i].a;
			if (!a.h || seenU.has(a.u) || (S.me && a.u === S.me.u) || S.blocks.has(a.u) || a.s === 3) continue;
			seenU.add(a.u);
			out.push(a.h);
		}
		return out;
	}

	const staff = () => !!(S.me && S.me.staff > 0);
	function sanctionActive() {
		const s = S.me && S.me.sanction;
		return s && s.kind === "timeout" && num(s.until) > serverNow() ? s : null;
	}
	function lockdownLevel() { const l = S.lockdown; return l.level && (!l.until || l.until > serverNow()) ? l.level : 0; }

	// Why the composer is read-only in a room, or null when you can post.
	function blockReason(id) {
		const r = room(id);
		if (!r || !S.me) return "closed";
		if (S.mode === "readonly") return "readonly";
		if (sanctionActive()) return "timeout";
		if (r.retired) return "retired";
		if (staff()) return null;
		if (r.kind !== "room" && r.kind !== "quiet") return "announce";
		if (r.locked) return "locked";
		const lv = lockdownLevel();
		if (lv >= 3) return "lockdown3";
		if (lv >= 1 && (S.me.probation || S.lockdownHit)) return "lockdown";
		if (r.canPost === false) return lv ? "lockdown" : "locked";
		return null;
	}
	// Reactions follow the same rules as posting (the server refuses them in a locked room or
	// under lockdown), except in announcements, where everyone reacts.
	function canReact(id) {
		const why = blockReason(id);
		return !why || why === "announce";
	}
	// Quiet Focus during a focus block: one short check-in, unless you're staff.
	function checkIn(id) {
		const r = room(id);
		return !!r && r.kind === "quiet" && !!r.tmr && r.tmr.ph === "focus" && !staff();
	}

	function canEdit(m) {
		return !!S.me && m.a.u === S.me.u && m.st === 0 && !m.fr && m.k !== 1 && m.revs < MAX_EDITS && serverNow() - m.at < S.limits.editWindowS * 1000;
	}
	function lastEditable(id) {
		const rows = list(id);
		for (let i = rows.length - 1; i >= 0; i--) if (S.me && rows[i].a.u === S.me.u) return canEdit(rows[i]) ? rows[i] : null;
		return null;
	}

	function sinceFor(id) {
		const b = S.bufs.get(id);
		return b && b.loaded && !b.detached && b.useq > 0 ? b.useq : undefined;
	}
	const unread = (r) => r.id !== S.focus && r.head > r.read;

	// Reactions: optimistic toggle, then the ack's counts and your own set win.
	function toggleMine(id, e, on) {
		const row = findRow(id);
		const set = S.mine.get(id) || new Set();
		if (on === set.has(e)) return;
		if (on) set.add(e); else set.delete(e);
		S.mine.set(id, set);
		if (row) row.x[e] = Math.max(0, row.x[e] + (on ? 1 : -1));
	}
	function ackReact(d) {
		const row = findRow(d.id);
		if (row && "x" in d) row.x = normX(d.x);
		if (Array.isArray(d.mine)) S.mine.set(d.id, new Set(d.mine));
	}
	const mineHas = (id, e) => { const s = S.mine.get(id); return !!s && s.has(e); };
	function findRow(id) {
		for (const b of S.bufs.values()) { const m = b.by.get(id); if (m) return m; }
		return null;
	}

	// Outbox: optimistic rows for sends not yet acked (kept across reconnects, replayed after ready).
	// The caller's object itself is held, so its `state` updates show in the view.
	function addPending(p) {
		if (S.pending.length >= OUTBOX_MAX) return false;
		p.at = serverNow();
		S.pending.push(p);
		return true;
	}
	function removePending(cid) { const i = S.pending.findIndex((p) => p.cid === cid); if (i === -1) return null; return S.pending.splice(i, 1)[0]; }
	const pendingIn = (id) => S.pending.filter((p) => p.room === id);
	// The rows in a room that reply to message `id` (their previews follow its edits).
	const repliesTo = (roomId, id) => list(roomId).filter((m) => m.rp && m.rp.id === id);

	return {
		S, serverNow, room, buf, list, ready, hist, msg, upd, roomEv, heads, here, read, you, lockdown, typing, typingLine,
		tmr, setLease, busiest, myVote, setVote, checkIn,
		blockedHandles, authors, staff, sanctionActive, lockdownLevel, blockReason, canReact, canEdit, lastEditable, sinceFor, unread,
		toggleMine, ackReact, mineHas, findRow, addPending, removePending, pendingIn, repliesTo,
	};
}
